import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Radar,
  FileSearch,
  LineChart,
  Mail,
  ArrowRight,
} from "lucide-react";

const heroImage = "/Images/services/enviromental/consultancy/consultoria.webp";
const features = [
  {
    icon: ShieldCheck,
    title: "Cumplimiento normativo",
    description:
      "Verificamos y aseguramos el cumplimiento de la normativa ambiental vigente.",
  },
  {
    icon: Radar,
    title: "Planes de monitoreo",
    description:
      "Diseñamos planes específicos para aire, agua, suelo, ruido e hidrobiología.",
  },
  {
    icon: LineChart,
    title: "Análisis técnico",
    description:
      "Interpretamos resultados y entregamos información ambiental clara y confiable.",
  },
  {
    icon: FileSearch,
    title: "Informes y mejora",
    description:
      "Elaboramos informes técnicos y recomendaciones para optimizar tu gestión ambiental.",
  },
];

const consultingItems = [
  {
    number: "01",
    title: "Anticipa riesgos",
    description:
      "Detectamos riesgos ambientales antes de que impacten tu operación.",
  },
  {
    number: "02",
    title: "Decisiones informadas",
    description: "Basadas en análisis técnico y datos confiables.",
  },
  {
    number: "03",
    title: "Mejor desempeño",
    description:
      "Optimización del rendimiento ambiental con estrategias claras.",
  },
  {
    number: "04",
    title: "Sostenibilidad",
    description:
      "Fortalecemos la continuidad y cumplimiento ambiental a largo plazo.",
  },
];

const Consultancy = () => {
  return (
    <>
      {/* HERO */}
      <section className="overflow-hidden">
        <div className="relative h-[420px] sm:h-[500px] md:h-[620px] overflow-hidden">
          <img
            src={heroImage}
            alt="Consultoría Ambiental"
            className="w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-black/50" />

          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-transparent" />

          <div className="absolute inset-0 flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="max-w-2xl">
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ee7d32]/15 border border-[#ee7d32]/30 text-[#ee7d32] text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] backdrop-blur-sm">
                  Servicios
                </span>

                <h1 className="mt-6 text-4xl sm:text-5xl md:text-6xl xl:text-7xl font-bold text-white leading-[0.95]">
                  Consultoría <br />
                  <span className="text-[#ee7d32]">Ambiental</span>
                </h1>

                <p className="hidden md:block mt-6 text-white/80 text-sm sm:text-base md:text-lg leading-7 max-w-xl">
                  Acompañamos a tu organización con soluciones técnicas para
                  garantizar cumplimiento normativo, sostenibilidad y toma de
                  decisiones ambientales más eficientes.
                </p>

                <div className="mt-8 flex flex-wrap gap-3">
                  {[
                    "Cumplimiento normativo",
                    "Monitoreo ambiental",
                    "Análisis técnico",
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="px-4 py-2 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm text-white/90 text-sm"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONSULTORIA */}
      <section className="relative py-20 md:py-20 bg-[#f8f7f4] overflow-hidden">
        <div className="absolute top-0 right-0 w-[420px] h-[420px] bg-[#ee7d32]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[350px] h-[350px] bg-[#ee7d32]/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />
            <h2 className="mt-6 text-4xl sm:text-5xl md:text-6xl font-bold text-[#111] leading-[1]">
              Consultoría <span className="text-[#ee7d32]">Ambiental</span>
            </h2>

            <p className="mt-6 text-base sm:text-lg text-[#555] leading-8 max-w-2xl">
              Acompañamos a tu organización con estrategias, monitoreo y
              análisis especializados para fortalecer el cumplimiento normativo
              y la sostenibilidad ambiental.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mt-16 py-8">
            {features.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="group relative bg-white rounded-3xl p-8 pt-14 text-center shadow-[0_20px_60px_rgba(0,0,0,0.06)] hover:shadow-[0_30px_80px_rgba(243,112,33,0.18)] transition-all duration-500 hover:-translate-y-2"
                >
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-[#ee7d32] rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform duration-300">
                    <Icon
                      className="w-8 h-8"
                      strokeWidth={2.2}
                      stroke="white"
                    />
                  </div>

                  <h3 className="text-[20px] font-[800] text-[#111]">
                    {item.title}
                  </h3>

                  <p className="text-gray-600 leading-[1.7] text-sm">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PORQUE CONSULTORIA  */}
      <section className="relative py-20 md:py-20 bg-white overflow-hidden">
        <div className="absolute top-0 left-0 w-[380px] h-[380px] bg-[#ee7d32]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#ee7d32]/5 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-14 h-[4px] bg-[#ee7d32] rounded-full mb-5" />

            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#111] leading-[1.05]">
              ¿Por qué nuestra{" "}
              <span className="text-[#ee7d32]">consultoría</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-14 gap-y-10 mt-16">
            {consultingItems.map((item, index) => (
              <div
                key={index}
                className="group flex items-start gap-5 border-b border-gray-200 pb-8"
              >
                <div className="flex-shrink-0">
                  <span className="text-4xl md:text-5xl font-black text-[#ee7d32]/15 group-hover:text-[#ee7d32] transition-colors duration-500">
                    {item.number}
                  </span>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-[#111] mb-2 group-hover:text-[#ee7d32] transition-colors">
                    {item.title}
                  </h3>

                  <p className="text-[15px] leading-[1.8] text-gray-600">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA CONSULTORÍA / ANÁLISIS */}
      <section className="relative py-10 md:py-10 bg-white overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="group relative overflow-hidden rounded-[30px] border border-[#ececec] bg-white shadow-[0_12px_40px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_20px_60px_rgba(0,0,0,0.10)]">
            <div className="absolute top-0 left-0 h-full w-1.5 bg-gradient-to-b from-[#ee7d32] via-[#ee7d32]/50 to-transparent" />

            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10 px-6 py-8 md:px-10 md:py-10">
              <div className="flex flex-col sm:flex-row items-start gap-6 max-w-3xl">
                <div className="relative flex-shrink-0">
                  <div className="absolute inset-0 bg-[#ee7d32]/20 blur-xl rounded-2xl" />
                  <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-2xl flex items-center justify-center">
                    <Mail size={40} className="stroke-[#ee7d32]" />
                  </div>
                </div>

                <div>
                  <span className="inline-flex items-center rounded-full bg-[#ee7d32]/10 border border-[#ee7d32]/20 px-3 py-1 text-[11px] md:text-[12px] font-semibold uppercase tracking-widest text-[#ee7d32]">
                    Consultoría técnica especializada
                  </span>

                  <h3 className="mt-3 text-2xl sm:text-3xl font-bold text-[#111] leading-tight">
                    Agenda una asesoría con nuestros expertos
                  </h3>

                  <p className="mt-4 text-sm md:text-base leading-7 text-[#6b7280] max-w-2xl">
                    Analizamos tu operación, identificamos oportunidades de
                    mejora y diseñamos estrategias ambientales y energéticas
                    adaptadas a tu empresa.
                  </p>
                </div>
              </div>

              <div className="flex w-full lg:w-auto">
                <Link
                  to="/contact-us?division=ambiental&service=Consultoría ambiental"
                  className="group/btn inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-lg bg-[#ee7d32] px-6 py-3.5 md:px-7 md:py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#e56f20] hover:shadow-lg hover:shadow-[#ee7d32]/20"
                >
                  Solicitar consultoría
                  <ArrowRight
                    size={18}
                    className="transition-transform duration-300 group-hover/btn:translate-x-1"
                    color="white"
                  />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Consultancy;
